import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import './CookieConsent.css';

const CookieConsent = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true, // Always true, cannot be disabled
    functional: true,
    analytics: false,
    marketing: false,
  });

  useEffect(() => {
    const consent = localStorage.getItem('cookie_consent');
    if (!consent) {
      setShowBanner(true);
    } else {
      const savedPreferences = JSON.parse(consent);
      setPreferences(savedPreferences);
    }
  }, []);

  const handleAcceptAll = () => {
    const allAccepted = {
      necessary: true,
      functional: true,
      analytics: true,
      marketing: true,
    };
    localStorage.setItem('cookie_consent', JSON.stringify(allAccepted));
    localStorage.setItem('cookie_consent_date', new Date().toISOString());
    setPreferences(allAccepted);
    setShowBanner(false);
    setShowSettings(false);
  };

  const handleRejectAll = () => {
    const onlyNecessary = {
      necessary: true,
      functional: false,
      analytics: false,
      marketing: false,
    };
    localStorage.setItem('cookie_consent', JSON.stringify(onlyNecessary));
    localStorage.setItem('cookie_consent_date', new Date().toISOString());
    setPreferences(onlyNecessary);
    setShowBanner(false);
    setShowSettings(false);
  };

  const handleSavePreferences = () => {
    localStorage.setItem('cookie_consent', JSON.stringify(preferences));
    localStorage.setItem('cookie_consent_date', new Date().toISOString());
    setShowBanner(false);
    setShowSettings(false);
  };

  const handleTogglePreference = (key) => {
    if (key === 'necessary') return; // Cannot disable necessary cookies
    setPreferences((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  if (!showBanner) return null;

  return (
    <>
      <div className="cookie-overlay" onClick={() => setShowSettings(false)} />
      <div className="cookie-consent-banner">
        <div className="cookie-content">
          <div className="cookie-icon">🍪</div>
          <div className="cookie-text">
            <h3>We use cookies</h3>
            <p>
              We use cookies and similar technologies to enhance your browsing experience, 
              analyze site traffic, and provide personalized content. You can manage your 
              cookie preferences at any time. Learn more in our{' '}
              <Link to="/privacy-policy" className="cookie-link">Privacy Policy</Link>.
            </p>
          </div>
          <div className="cookie-actions">
            <button onClick={handleRejectAll} className="btn btn-reject">
              Reject All
            </button>
            <button onClick={() => setShowSettings(!showSettings)} className="btn btn-settings">
              Cookie Settings
            </button>
            <button onClick={handleAcceptAll} className="btn btn-accept">
              Accept All
            </button>
          </div>
        </div>

        {showSettings && (
          <div className="cookie-settings">
            <h4>Cookie Preferences</h4>
            <div className="cookie-options">
              <div className="cookie-option">
                <div className="cookie-option-header">
                  <label>
                    <input
                      type="checkbox"
                      checked={preferences.necessary}
                      disabled
                    />
                    <span className="cookie-option-title">Strictly Necessary</span>
                  </label>
                  <span className="cookie-badge">Always Active</span>
                </div>
                <p className="cookie-option-description">
                  These cookies are essential for the website to function properly. 
                  They enable core functionality such as shopping cart and admin authentication.
                </p>
              </div>

              <div className="cookie-option">
                <div className="cookie-option-header">
                  <label>
                    <input
                      type="checkbox"
                      checked={preferences.functional}
                      onChange={() => handleTogglePreference('functional')}
                    />
                    <span className="cookie-option-title">Functional Cookies</span>
                  </label>
                </div>
                <p className="cookie-option-description">
                  These cookies enable enhanced functionality and personalization, 
                  such as remembering your preferences and settings.
                </p>
              </div>

              <div className="cookie-option">
                <div className="cookie-option-header">
                  <label>
                    <input
                      type="checkbox"
                      checked={preferences.analytics}
                      onChange={() => handleTogglePreference('analytics')}
                    />
                    <span className="cookie-option-title">Analytics Cookies</span>
                  </label>
                </div>
                <p className="cookie-option-description">
                  These cookies help us understand how visitors interact with our website 
                  by collecting and reporting information anonymously.
                </p>
              </div>

              <div className="cookie-option">
                <div className="cookie-option-header">
                  <label>
                    <input
                      type="checkbox"
                      checked={preferences.marketing}
                      onChange={() => handleTogglePreference('marketing')}
                    />
                    <span className="cookie-option-title">Marketing Cookies</span>
                  </label>
                </div>
                <p className="cookie-option-description">
                  These cookies are used to track visitors across websites to display 
                  relevant advertisements and marketing campaigns.
                </p>
              </div>
            </div>
            <div className="cookie-settings-actions">
              <button onClick={handleSavePreferences} className="btn btn-save">
                Save Preferences
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default CookieConsent;
